<template>
  <CompanyAppLayout
    page-title="Field Tickets"
    page-subtitle="Capture labor, equipment, and materials — approve and invoice"
  >
    <div class="space-y-6 pt-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-white">Field Tickets</h1>
          <p class="text-slate-400 text-sm mt-1">
            Wellsite tickets from draft → company-man approval → invoice.
          </p>
        </div>
        <button class="btn-primary" @click="openCreate">+ New Field Ticket</button>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="stat-card">
          <p class="text-sm text-slate-400">Total</p>
          <p class="text-3xl font-bold text-white mt-1">{{ stats.total || 0 }}</p>
        </div>
        <div class="stat-card">
          <p class="text-sm text-amber-400">Pending Approval</p>
          <p class="text-3xl font-bold text-white mt-1">{{ stats.pendingApproval || 0 }}</p>
        </div>
        <div class="stat-card">
          <p class="text-sm text-emerald-400">Approved</p>
          <p class="text-3xl font-bold text-white mt-1">{{ stats.approved || 0 }}</p>
        </div>
        <div class="stat-card">
          <p class="text-sm text-cyan-400">Billable / Invoiced</p>
          <p class="text-3xl font-bold text-white mt-1">${{ Number(stats.billedAmount || 0).toLocaleString() }}</p>
        </div>
      </div>

      <div class="panel">
        <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 class="text-lg font-semibold text-white">Tickets</h2>
          <div class="flex gap-2 flex-wrap">
            <input v-model="search" type="text" placeholder="Search…" class="form-input w-48" />
            <select v-model="filterStatus" class="form-select">
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="invoiced">Invoiced</option>
            </select>
          </div>
        </div>

        <div v-if="loading" class="text-slate-400 py-8 text-center">Loading…</div>
        <div v-else-if="!filtered.length" class="text-slate-400 py-10 text-center">No field tickets yet.</div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 pr-3 text-left">Ticket</th>
                <th class="py-2 pr-3 text-left">Customer / Site</th>
                <th class="py-2 pr-3 text-left">Hours</th>
                <th class="py-2 pr-3 text-left">Total</th>
                <th class="py-2 pr-3 text-left">Status</th>
                <th class="py-2 pr-3 text-left">Created</th>
                <th class="py-2 pr-3 text-left"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in filtered" :key="t.id" class="border-b border-slate-800/80 text-slate-200">
                <td class="py-3 pr-3">
                  <div class="font-medium text-white">{{ t.number }}</div>
                  <div class="text-xs text-slate-500">{{ t.title }}</div>
                </td>
                <td class="py-3 pr-3">
                  <div>{{ t.customerName || '—' }}</div>
                  <div class="text-xs text-slate-500">{{ t.siteName || '' }}</div>
                </td>
                <td class="py-3 pr-3">{{ t.laborHours || 0 }}</td>
                <td class="py-3 pr-3 text-emerald-400">${{ Number(t.totalAmount || 0).toLocaleString() }}</td>
                <td class="py-3 pr-3">
                  <span class="status-pill" :class="'st-' + t.status">{{ statusLabel(t.status) }}</span>
                </td>
                <td class="py-3 pr-3">{{ t.createdAt }}</td>
                <td class="py-3 pr-3">
                  <router-link :to="`/company/field-tickets/${t.id}`" class="text-emerald-400 hover:text-emerald-300 font-medium">
                    Open
                  </router-link>
                  <button
                    v-if="['open', 'in_progress'].includes(t.status)"
                    class="ml-3 text-red-400 hover:text-red-300 font-medium disabled:opacity-50"
                    :disabled="deletingId === t.id"
                    @click="deleteTicket(t)"
                  >
                    {{ deletingId === t.id ? 'Deleting…' : 'Delete' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showCreate" class="modal-backdrop" @click.self="showCreate = false">
      <div class="modal-panel max-w-2xl">
        <h3 class="text-xl font-semibold text-white mb-4">New Field Ticket</h3>
        <form class="space-y-4" @submit.prevent="createTicket">
          <div>
            <label class="form-label">Title *</label>
            <input v-model="form.title" required class="form-input" placeholder="Pad 7 vacuum / swab" />
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="form-label">Customer / Operator</label>
              <input v-model="form.customerName" class="form-input" placeholder="Operator name" />
            </div>
            <div>
              <label class="form-label">Field Site</label>
              <select v-model="form.siteId" class="form-select" @change="onSiteChange">
                <option :value="0">Select site…</option>
                <option v-for="s in sites" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="form-label">Company Man</label>
              <input v-model="form.companyManName" class="form-input" />
            </div>
            <div>
              <label class="form-label">Company Man Email</label>
              <input v-model="form.companyManEmail" type="email" class="form-input" />
            </div>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="form-label">Labor Hours</label>
              <input v-model.number="form.laborHours" type="number" min="0" step="0.25" class="form-input" />
            </div>
            <div>
              <label class="form-label">Labor Rate</label>
              <input v-model.number="form.laborRate" type="number" min="0" step="0.01" class="form-input" />
            </div>
            <div>
              <label class="form-label">Materials $</label>
              <input v-model.number="form.materialsCost" type="number" min="0" step="0.01" class="form-input" />
            </div>
          </div>
          <div v-if="pricebook.length" class="text-xs text-slate-500">
            Price book:
            <button
              v-for="p in pricebook"
              :key="p.id"
              type="button"
              class="ml-2 text-emerald-400 hover:underline"
              @click="applyRate(p)"
            >{{ p.name }} ${{ p.rate }}/{{ p.unit }}</button>
          </div>
          <div>
            <label class="form-label">Description / Notes</label>
            <textarea v-model="form.description" rows="3" class="form-input"></textarea>
          </div>
          <p v-if="formError" class="text-red-400 text-sm">{{ formError }}</p>
          <div class="flex justify-end gap-3">
            <button type="button" class="btn-secondary" @click="showCreate = false">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving">{{ saving ? 'Creating…' : 'Create Ticket' }}</button>
          </div>
        </form>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { buildApiUrl } from '@/config/api'

const router = useRouter()
const loading = ref(true)
const error = ref('')
const tickets = ref<any[]>([])
const sites = ref<any[]>([])
const pricebook = ref<any[]>([])
const stats = reactive<any>({ total: 0, pendingApproval: 0, approved: 0, invoiced: 0, billedAmount: 0 })
const search = ref('')
const filterStatus = ref('')
const showCreate = ref(false)
const saving = ref(false)
const deletingId = ref<number | null>(null)
const formError = ref('')

const form = reactive({
  title: '',
  customerName: '',
  siteId: 0,
  siteName: '',
  companyManName: '',
  companyManEmail: '',
  laborHours: 8,
  laborRate: 85,
  materialsCost: 0,
  description: '',
})

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return tickets.value.filter((t) => {
    if (filterStatus.value && t.status !== filterStatus.value) return false
    if (!q) return true
    return [t.number, t.title, t.customerName, t.siteName].some((x) => String(x || '').toLowerCase().includes(q))
  })
})

function statusLabel(s: string) {
  return ({ open: 'Open', in_progress: 'In progress', submitted: 'Submitted', approved: 'Approved', invoiced: 'Invoiced', closed: 'Closed' } as any)[s] || s
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'Content-Type': 'application/json' }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl('company/field-tickets'), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load')
    tickets.value = data.fieldTickets || []
    sites.value = data.sites || []
    pricebook.value = data.pricebook || []
    Object.assign(stats, data.stats || {})
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

function openCreate() {
  formError.value = ''
  showCreate.value = true
}

function onSiteChange() {
  const s = sites.value.find((x) => x.id === Number(form.siteId))
  form.siteName = s?.name || ''
}

function applyRate(p: any) {
  form.laborRate = Number(p.rate) || form.laborRate
}

async function createTicket() {
  saving.value = true
  formError.value = ''
  try {
    const res = await fetch(buildApiUrl('company/field-tickets'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        ...form,
        siteId: form.siteId || null,
      }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Create failed')
    showCreate.value = false
    await router.push(`/company/field-tickets/${data.id}`)
  } catch (e: any) {
    formError.value = e.message || 'Create failed'
  } finally {
    saving.value = false
  }
}

async function deleteTicket(ticket: any) {
  if (!window.confirm(`Delete field ticket ${ticket.number || ''}? This cannot be undone.`)) return
  deletingId.value = Number(ticket.id)
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`company/field-tickets/${ticket.id}`), {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Delete failed')
    tickets.value = tickets.value.filter((item) => item.id !== ticket.id)
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    deletingId.value = null
  }
}

onMounted(load)
</script>

<style scoped>
.panel { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6; }
.stat-card { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4; }
.form-label { @apply block text-sm text-slate-400 mb-1; }
.form-input, .form-select { @apply w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40; }
.btn-primary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium; }
.btn-secondary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-slate-700/60 border border-slate-600 text-slate-200; }
.modal-backdrop { @apply fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4; }
.modal-panel { @apply w-full bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl; }
.status-pill { @apply inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium; }
.st-open { @apply bg-slate-500/20 text-slate-300; }
.st-in_progress { @apply bg-cyan-500/20 text-cyan-300; }
.st-submitted { @apply bg-amber-500/20 text-amber-300; }
.st-approved { @apply bg-emerald-500/20 text-emerald-300; }
.st-invoiced { @apply bg-violet-500/20 text-violet-300; }
.st-closed { @apply bg-slate-600/40 text-slate-400; }
</style>
